/**
 * The exam session (brief §exam: `/exam/session`). Countdown from the
 * server's deadline, a problem navigator, "solve on paper" instruction, a
 * small final-answer field + optional notes per problem, autosave (debounced
 * 3 s + on blur + on problem change + `beforeunload`) mirrored to
 * `localStorage`, and submit with confirmation. Auto-submits on expiry. The
 * student can also abandon the sitting ("Avbryt övningstentan", confirmed in
 * a dialog): it is closed without being handed in, graded or resumed.
 */
import { Check } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { useExamMode } from "@client/app/examMode";
import { Figure } from "@client/components/Figure";
import { Localized } from "@client/components/Localized";
import { QuestionParts } from "@client/components/QuestionParts";
import { ReportIssue } from "@client/components/ReportIssue";
import { SourceBadge, TranslationPendingBadge } from "@client/components/SourceBadge";
import {
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Spinner,
  Textarea,
  useToast,
} from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { isCode, messageForError } from "@client/lib/errors";
import { formatDuration, formatNumber, formatPoints } from "@client/lib/format";
import { useAbandonExam, useCurrentExam, useSaveExamAnswers, useSubmitExam } from "./api";
import { countdownLevel, crossedAnnounceThreshold } from "./countdown";
import { clearDraft, examDraftGeneration, hasUnsavedChanges, loadDraft, saveDraft } from "./draft";
import type { AnswerEntry, CurrentResponse, LastPlanDto, SessionDto } from "./types";
import { hideLastPlan, isLastPlanHidden } from "./wrapper/draft";
import { LastPlanNote } from "./wrapper/LastPlanNote";

const SAVE_DEBOUNCE_MS = 3000;
/**
 * A resync from the server nudges the countdown only when the client clock
 * has drifted by at least this much — smaller differences are just request
 * latency and would otherwise make the timer visibly twitch every autosave.
 */
const RESYNC_THRESHOLD_MS = 2000;
/** Backoff between autosave retries after a failure (network or 5xx); capped. */
const SAVE_RETRY_BACKOFF_MS = [3000, 6000, 12000, 24000, 30000];
/**
 * How long a detected clock jump may hold off auto-submit while waiting for
 * the resync it triggers (rereview-O2.md R2-1): a refetch that errors or
 * never lands must not suppress auto-submit forever.
 */
const CLOCK_JUMP_HOLD_TIMEOUT_MS = 10_000;
/**
 * How long the spoken time-warning stays in the live region before it is
 * cleared back to empty (review-A Important 2). Long enough for a screen
 * reader to have announced it, short enough that it does not sit stale in
 * the reading order for the rest of the warning band.
 */
const ANNOUNCEMENT_CLEAR_MS = 10_000;

type FlushOutcome = {
  kind:
    | "saved"
    | "retryable-error"
    | "auth-required"
    | "expired"
    | "closed"
    | "abandoning"
    | "unmounted";
};

interface CountdownAnchor {
  remainingMs: number;
  /** `performance.now()` at the anchor — monotonic, but may not advance while the OS sleeps. */
  perfAt: number;
  /** `Date.now()` at the anchor — always advances across a sleep, but can jump on a clock change. */
  wallAt: number;
}

/**
 * Elapsed time since `anchor`, taking the larger of the two clocks' deltas
 * (review-O.md Important 3b). After a laptop sleep `performance.now()` can
 * under-report how long the machine was actually suspended, which would
 * otherwise show more time left than really remains; taking the max never
 * lets the countdown overstate what is left, only understate it (corrected
 * moments later by the next autosave/query resync).
 */
function elapsedSinceAnchor(anchor: CountdownAnchor): number {
  return Math.max(performance.now() - anchor.perfAt, Date.now() - anchor.wallAt);
}

export function ExamSessionPage() {
  const { setExamActive } = useExamMode();
  const currentQuery = useCurrentExam();

  /**
   * The id of the session being sat. `GET /api/exam/current` stops returning a
   * session the moment it is submitted, so without this the student is bounced
   * back to the lobby by the guard below instead of reaching the grading page
   * — both when they hand in and when the timer auto-submits.
   */
  const lastSessionId = useRef<string | null>(null);
  /**
   * True from the moment the student confirms "Avbryt övningstentan" until the
   * request fails (the child resets it). `GET /current` answers 404 as soon as
   * the server has abandoned the paper, which can land before the abandon's own
   * response: this keeps that 404 from reading as "handed in, go and grade it".
   */
  const abandoningRef = useRef(false);
  // A submit invalidates /current before mutateAsync settles. Hold the active
  // sitting until its controller finishes draft cleanup and navigation.
  const closingRef = useRef(false);
  const lastCurrentData = useRef(currentQuery.data);
  if (currentQuery.data) lastCurrentData.current = currentQuery.data;
  const activeData = currentQuery.data ?? (closingRef.current ? lastCurrentData.current : null);
  const currentSessionId = currentQuery.data?.session.id ?? null;
  useEffect(() => {
    if (currentSessionId) lastSessionId.current = currentSessionId;
  }, [currentSessionId]);

  useEffect(() => {
    setExamActive(true);
    return () => setExamActive(false);
  }, [setExamActive]);

  /**
   * Review-A Important 4: with only the badge+timer group sticky (not the
   * whole instruction row), a field focused while tabbing upwards — or a
   * fragment-link jump — can still land underneath that sticky bar plus the
   * app shell's own sticky header. `scroll-padding-top` on the scrolling box
   * (the document itself here — `<main>` has no `overflow` of its own) keeps
   * it in view (WCAG 2.4.11). Scoped to this page only, and restored on
   * unmount, rather than a global rule in a shared stylesheet this feature
   * does not own.
   */
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.scrollPaddingTop;
    root.style.scrollPaddingTop = "8rem";
    return () => {
      root.style.scrollPaddingTop = previous;
    };
  }, []);

  /**
   * Re-syncs from the server when the tab regains focus or the machine comes
   * back online (review-O.md Important 3a): `refetchOnWindowFocus` is off
   * globally, and without this a laptop sleep or a lost connection leaves the
   * countdown anchored to whatever it last saw until the student next types.
   * `refetchRef` avoids re-subscribing every render — `refetch`'s identity is
   * not guaranteed stable across renders.
   */
  const refetchRef = useRef(currentQuery.refetch);
  refetchRef.current = currentQuery.refetch;
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") void refetchRef.current();
    }
    function onOnline() {
      void refetchRef.current();
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  if (currentQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!activeData) {
    if (abandoningRef.current) {
      return <Navigate to="/exam" replace state={{ abandoned: true }} />;
    }
    return lastSessionId.current ? (
      <Navigate to={`/exam/${lastSessionId.current}/grade`} replace />
    ) : (
      <Navigate to="/exam" replace />
    );
  }

  const { session } = activeData;
  if (session.status !== "in_progress") {
    return <Navigate to={`/exam/${session.id}/grade`} replace />;
  }

  return (
    <ActiveExamSession
      key={session.id}
      session={session}
      lastPlan={activeData.lastPlan ?? null}
      abandoningRef={abandoningRef}
      closingRef={closingRef}
      refetchSession={currentQuery.refetch}
      sessionDataUpdatedAt={currentQuery.dataUpdatedAt}
    />
  );
}

function ActiveExamSession({
  session,
  lastPlan,
  abandoningRef,
  closingRef,
  refetchSession,
  sessionDataUpdatedAt,
}: {
  session: SessionDto;
  lastPlan: LastPlanDto | null;
  /** Owned by the parent (see there); set synchronously by the confirm handler. */
  abandoningRef: MutableRefObject<boolean>;
  /** Holds the parent's /current guard during lifecycle completion. */
  closingRef: MutableRefObject<boolean>;
  /**
   * Re-fetches `/api/exam/current`; used to resync after a detected clock
   * jump (see below). `cancelRefetch: false` (rereview-O2.md R2-1) so a
   * second call while one is still in flight cannot cancel it — the tick
   * effect never fires a second one anyway (it checks the hold first), but
   * this keeps the guarantee even if that ever changes. The resolved
   * `status`/`dataUpdatedAt` tell a fresh success apart from an error, a
   * cancelled/superseded fetch, or the same stale cached data handed back
   * again (rereview-O3.md R3-1) — `refetch()` resolves rather than rejects
   * in every one of those cases under this app's default query options.
   */
  refetchSession: (options?: { cancelRefetch?: boolean }) => Promise<
    | {
        status?: string;
        dataUpdatedAt?: number;
        data?: CurrentResponse | null;
      }
    | undefined
  >;
  /** `currentQuery.dataUpdatedAt` — when the session data was last *successfully* fetched. */
  sessionDataUpdatedAt: number;
}) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const navigate = useNavigate();
  const toast = useToast();
  const saveMutation = useSaveExamAnswers(session.id);
  const submitMutation = useSubmitExam(session.id);
  const abandonMutation = useAbandonExam(session.id);

  const [answers, setAnswers] = useState<Record<string, AnswerEntry>>(session.answers);
  const [index, setIndex] = useState(0);
  /** Forces a render once a second so the countdown (below) ticks down. */
  const [tick, setTick] = useState(0);
  /** What the autosave line says. Generations are refs, so they cannot drive it. */
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  /** A failed autosave: a visible warning, distinct from the quiet "Sparar…" line. */
  const [saveWarning, setSaveWarning] = useState<"network" | "expired" | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [abandonOpen, setAbandonOpen] = useState(false);
  /**
   * Mirrors `abandoningRef` for rendering and for re-running the auto-submit
   * check once a failed abandon releases its hold (a ref change re-renders nothing).
   */
  const [abandoning, setAbandoning] = useState(false);
  const [restorePrompt, setRestorePrompt] = useState<Record<string, AnswerEntry> | null>(null);
  const [planHidden, setPlanHidden] = useState(() => isLastPlanHidden(session.id));

  const draftGenerationRef = useRef(examDraftGeneration());
  function ownsDraftLifetime() {
    return draftGenerationRef.current === examDraftGeneration();
  }
  const revisionRef = useRef(session.revision);
  const answersRef = useRef(answers);
  const editGeneration = useRef(0);
  const confirmedGeneration = useRef(0);
  const drainPromiseRef = useRef<Promise<FlushOutcome> | null>(null);
  const terminalSaveOutcome = useRef<FlushOutcome | null>(null);
  const [closing, setClosing] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttempt = useRef(0);
  const autoSubmitted = useRef(false);
  const instructionRef = useRef<HTMLParagraphElement>(null);

  /**
   * The countdown never trusts `Date.now()` (spec §4.5, M2 fix): a client
   * clock that runs fast hands the paper in early, one that runs slow keeps
   * the student typing past the real deadline. `performance.now()` is
   * monotonic and immune to clock changes, so the countdown is anchored to
   * the server's `remainingSeconds` (fresh on load, re-synced from every
   * autosave response) and only ever advances by elapsed *wall time since
   * that anchor*, not by comparing two clock readings.
   */
  const anchorRef = useRef<CountdownAnchor>({
    remainingMs: session.remainingSeconds * 1000,
    perfAt: performance.now(),
    wallAt: Date.now(),
  });

  /**
   * Nudges the anchor to the server's clock — ignores sub-2s drift (network
   * jitter) unless `force`, which re-anchors regardless of the size of the
   * difference. `force` is for the clock-jump hold below (rereview-O2.md
   * R2-1): once that refetch lands, the anchor must reflect the server's
   * answer even when it *agrees* with the (jumped) local estimate — a
   * genuine sleep on a platform where `performance.now()` pauses is exactly
   * that case, and without forcing it the stale anchor would keep tripping
   * the same jump check every following tick.
   */
  const resyncCountdown = useCallback((serverRemainingSeconds: number, force = false) => {
    if (!Number.isFinite(serverRemainingSeconds)) return;
    const perfAt = performance.now();
    const wallAt = Date.now();
    const localRemainingMs = anchorRef.current.remainingMs - elapsedSinceAnchor(anchorRef.current);
    const serverRemainingMs = serverRemainingSeconds * 1000;
    if (force || Math.abs(serverRemainingMs - localRemainingMs) >= RESYNC_THRESHOLD_MS) {
      anchorRef.current = { remainingMs: serverRemainingMs, perfAt, wallAt };
    }
  }, []);

  /**
   * Re-stamps the anchor to *this instant*, keeping the local (never-
   * overstating) estimate as the remaining time (rereview-O3.md R3-1): the
   * clock-jump refetch's `.then`/`.catch` call this whenever it did not get
   * a fresh, successful answer — an error, a cancelled/superseded fetch, or
   * the same stale cached data handed back again all leave `data` set to
   * the *last successful* payload, and force-re-anchoring to that would
   * move the deadline later than the server's, exactly backwards from what
   * the fallback must do. A fresh `perfAt`/`wallAt` also stops the next
   * tick from immediately re-arming on the same, now-closed gap.
   */
  const reanchorToLocalEstimate = useCallback(() => {
    const remainingMs = anchorRef.current.remainingMs - elapsedSinceAnchor(anchorRef.current);
    anchorRef.current = { remainingMs, perfAt: performance.now(), wallAt: Date.now() };
  }, []);

  const clearRetryTimer = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  }, []);

  /**
   * Guards timer scheduling and the local draft write against a flush that
   * settles after this component has already unmounted (review-O.md Minor
   * 2): without it, a retry scheduled by a flush that fails *after* unmount
   * escapes the unmount cleanup below (which only clears whatever timer
   * exists at that instant) and keeps re-saving every `SAVE_RETRY_BACKOFF_MS`
   * step, rewriting the draft `submit` already cleared. Set in the effect
   * body, not only the initial ref value (rereview-O1.md N1): StrictMode
   * mounts, cleans up and re-runs every effect once in development, and a
   * cleanup-only assignment left this stuck at `false` for the page's whole
   * life on `npm run dev`, silently dropping every save and submit.
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * A forward clock jump (rereview-O1.md N5): `Math.max(perf, wall)` in
   * `elapsedSinceAnchor` means a `Date.now()` step bigger than the time left
   * — an NTP correction on a badly-set clock, or a manual change — would
   * otherwise drive `remainingMs` to zero or below and auto-submit on the
   * very next tick, before any resync could correct it. The tick effect
   * below sets this and fires one refetch; the auto-submit effect holds off
   * while it is set.
   *
   * React *state*, not only a ref (rereview-O2.md R2-1d): clearing a plain
   * ref does not itself cause the auto-submit effect to re-run, and relying
   * on it to be re-checked incidentally (because some other dependency of
   * that effect happens to change identity every render) is not a
   * guarantee. `resyncPendingRef` mirrors the state for the tick interval's
   * closure and the refetch's `.then`/`.catch`/timeout callbacks, which are
   * created once and would otherwise read a stale value.
   */
  const [resyncPending, setResyncPending] = useState(false);
  const resyncPendingRef = useRef(false);
  const clockJumpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setClockJumpHold = useCallback((pending: boolean) => {
    resyncPendingRef.current = pending;
    setResyncPending(pending);
  }, []);

  /** Idempotent: whichever of "the refetch settled" / "the bound elapsed" runs first wins. */
  const clearClockJumpHold = useCallback(() => {
    if (clockJumpTimeoutRef.current) {
      clearTimeout(clockJumpTimeoutRef.current);
      clockJumpTimeoutRef.current = null;
    }
    if (resyncPendingRef.current) setClockJumpHold(false);
  }, [setClockJumpHold]);

  // Re-syncs the countdown whenever the session query delivers fresh data —
  // not only on mount (review-O.md Important 3a): a background refetch (react
  // -query) or the explicit visibilitychange/online refetch above both land
  // here, since `session` (and so `session.remainingSeconds`) is a prop that
  // changes when the parent's query data changes. The clock-jump hold above
  // is cleared by its own refetch call below, not from here — this effect
  // also fires for the ordinary autosave/visibility resyncs, which must keep
  // the usual 2 s threshold rather than force an unconditional re-anchor.
  useEffect(() => {
    resyncCountdown(session.remainingSeconds);
  }, [session.remainingSeconds, resyncCountdown]);

  // Offer to restore a local draft that the server never confirmed.
  useEffect(() => {
    const draft = loadDraft(session.id);
    if (hasUnsavedChanges(draft, session.revision, session.answers)) {
      setRestorePrompt(draft!.answers);
    }
    // Only on mount — the session prop is stable for the lifetime of this component (`key={session.id}`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchSessionRef = useRef(refetchSession);
  refetchSessionRef.current = refetchSession;
  /** The last successful fetch's timestamp, for the "was this a fresh success?" check below. */
  const dataUpdatedAtRef = useRef(sessionDataUpdatedAt);
  dataUpdatedAtRef.current = sessionDataUpdatedAt;

  // Re-renders once a second so the countdown below ticks — it reads the
  // anchor (performance.now()-based), not `Date.now()`. Also watches for a
  // forward clock jump (N5): if the wall-clock delta has pulled far enough
  // ahead of the (monotonic) perf delta, `elapsedSinceAnchor`'s `Math.max`
  // would otherwise read straight off the jumped clock. Fire *one* refetch
  // per detected jump — skip while one is already pending (rereview-O2.md
  // R2-1a/b) — and hold off auto-submit until it settles, rather than
  // trusting the guess.
  useEffect(() => {
    const id = setInterval(() => {
      const perfDelta = performance.now() - anchorRef.current.perfAt;
      const wallDelta = Date.now() - anchorRef.current.wallAt;
      if (wallDelta - perfDelta > RESYNC_THRESHOLD_MS && !resyncPendingRef.current) {
        setClockJumpHold(true);
        // A refetch that errors, or simply never lands, must not suppress
        // auto-submit forever (R2-1c) — release the hold after a short
        // bound either way, trusting the local estimate from then on.
        clockJumpTimeoutRef.current = setTimeout(clearClockJumpHold, CLOCK_JUMP_HOLD_TIMEOUT_MS);
        const dataUpdatedAtBefore = dataUpdatedAtRef.current;
        // `cancelRefetch: false` (R2-1b): with the pending-check above this
        // is never actually racing another in-flight call today, but keeps
        // the guarantee if that ever changes.
        refetchSessionRef
          .current({ cancelRefetch: false })
          .then((result) => {
            const remainingSeconds = result?.data?.session?.remainingSeconds;
            // A fresh success only (rereview-O3.md R3-1): `refetch()`
            // resolves rather than rejects on an error, a cancelled or
            // superseded fetch, or a `TanStack Query` bail that hands back
            // the same *last successful* payload — every one of those cases
            // must never move the deadline later than the server's, so only
            // `status: "success"` with a strictly newer `dataUpdatedAt` is
            // trusted to re-anchor. `force: true` bypasses the 2 s threshold
            // for that one, still-fresh answer: the server can *agree* with
            // the jumped wall estimate — a genuine sleep on a platform where
            // `performance.now()` pauses is exactly that — and only a
            // forced re-anchor moves `wallAt` far enough forward to stop
            // the next tick from tripping the same check again.
            const isFreshSuccess =
              result?.status === "success" &&
              typeof result.dataUpdatedAt === "number" &&
              result.dataUpdatedAt > dataUpdatedAtBefore &&
              typeof remainingSeconds === "number";
            if (isFreshSuccess) {
              resyncCountdown(remainingSeconds, true);
            } else {
              reanchorToLocalEstimate();
            }
          })
          // `refetch()` never rejects under this app's default query options
          // (it resolves with an error result instead, handled above), but
          // falls back the same way if that ever changes.
          .catch(reanchorToLocalEstimate)
          .finally(clearClockJumpHold);
      }
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
    // `resyncCountdown`, `reanchorToLocalEstimate`, `setClockJumpHold` and
    // `clearClockJumpHold` are all `useCallback`s with their own stable
    // (`[]`-rooted) deps, so listing them here does not recreate the
    // interval on every render.
  }, [resyncCountdown, reanchorToLocalEstimate, setClockJumpHold, clearClockJumpHold]);

  const remainingMs = useMemo(
    () => anchorRef.current.remainingMs - elapsedSinceAnchor(anchorRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed on each tick, from the ref
    [tick],
  );
  const level = countdownLevel(remainingMs);

  /**
   * UX-01: the ticking digits are `role="timer"`, not a live region — an
   * `aria-live` element there would re-announce every second and drown the
   * problem text. Instead a separate, normally-empty `role="status"` speaks
   * only the instant the countdown crosses 30, 5 or 1 minute left, from the
   * same per-second tick that drives the digits.
   *
   * Review-A Important 2: the node itself must be mounted *before* it ever
   * gets text — a `role="status"` that appears already holding its first
   * warning is exactly the kind of live region NVDA/JAWS+Chrome and
   * VoiceOver often fail to announce, which would silently drop the very
   * first warning of every sitting. So the `<p>` below is always rendered,
   * starting empty, and this only ever calls `setAnnouncement` on a real
   * crossing. The text is then cleared back to empty a few seconds later,
   * so it does not sit stale in the reading order, and so re-crossing the
   * same threshold later (an upward resync) is a real change from "" again
   * and gets announced.
   */
  const [announcement, setAnnouncement] = useState("");
  const prevRemainingRef = useRef(remainingMs);
  const announcementClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const crossed = crossedAnnounceThreshold(prevRemainingRef.current, remainingMs);
    if (crossed !== null) {
      const minutes = Math.round(crossed / 60_000);
      setAnnouncement(
        tp("exam.session.timeWarning", minutes, { count: formatNumber(minutes, lang) }),
      );
      if (announcementClearRef.current) clearTimeout(announcementClearRef.current);
      announcementClearRef.current = setTimeout(() => {
        setAnnouncement("");
        announcementClearRef.current = null;
      }, ANNOUNCEMENT_CLEAR_MS);
    }
    prevRemainingRef.current = remainingMs;
  }, [remainingMs, tp, lang]);
  useEffect(
    () => () => {
      if (announcementClearRef.current) clearTimeout(announcementClearRef.current);
    },
    [],
  );

  /** POST is owned by the lifecycle controller below, never by a PUT callback. */
  const submitNow = useCallback(async () => {
    try {
      await submitMutation.mutateAsync();
      if (!mountedRef.current || !ownsDraftLifetime() || abandoningRef.current) return false;
      // A deadline may close with older stored answers. Keep its unconfirmed
      // local mirror, including an offered draft the student has not restored.
      const draft = loadDraft(session.id);
      if (
        editGeneration.current === confirmedGeneration.current &&
        (!draft || JSON.stringify(draft.answers) === JSON.stringify(answersRef.current))
      )
        clearDraft(session.id);
      else toast.show(t("exam.session.storedAnswersUsed"), "info");
      navigate(`/exam/${session.id}/grade`, {
        replace: true,
        state: autoSubmitted.current ? { autoSubmitted: true } : undefined,
      });
      return true;
    } catch (error) {
      if (mountedRef.current && !abandoningRef.current) toast.error(messageForError(error, t));
      return false;
    }
  }, [submitMutation, session.id, navigate, toast, t, abandoningRef]);

  /** Persist just the captured snapshot; later edits remain unconfirmed. */
  const persistSnapshot = useCallback(
    async (
      sentAnswers: Record<string, AnswerEntry>,
      sentGeneration: number,
    ): Promise<FlushOutcome> => {
      clearRetryTimer();
      try {
        const res = await saveMutation.mutateAsync({
          answers: sentAnswers,
          revision: revisionRef.current,
        });
        if (abandoningRef.current) return { kind: "abandoning" };
        if (!mountedRef.current || !ownsDraftLifetime()) return { kind: "unmounted" };
        revisionRef.current = res.revision;
        confirmedGeneration.current = sentGeneration;
        resyncCountdown(res.remainingSeconds);
        retryAttempt.current = 0;
        setSaveWarning(null);
        setSaveState(editGeneration.current === confirmedGeneration.current ? "saved" : "saving");
        // This is the latest local snapshot on top of the newly confirmed cursor,
        // not the older snapshot whose PUT just finished.
        saveDraft(session.id, {
          baseRevision: res.revision,
          answers: answersRef.current,
          updatedAt: Date.now(),
        });
        return { kind: "saved" };
      } catch (error) {
        if (abandoningRef.current) return { kind: "abandoning" };
        if (!mountedRef.current || !ownsDraftLifetime()) return { kind: "unmounted" };
        setSaveState("saving");
        saveDraft(session.id, {
          baseRevision: revisionRef.current,
          answers: answersRef.current,
          updatedAt: Date.now(),
        });
        if (isCode(error, "UNAUTHENTICATED")) {
          setSaveWarning("expired");
          terminalSaveOutcome.current = { kind: "auth-required" };
        } else if (isCode(error, "EXAM_EXPIRED")) {
          setSaveWarning(null);
          terminalSaveOutcome.current = { kind: "expired" };
        } else if (isCode(error, "EXAM_SUBMITTED")) {
          setSaveWarning(null);
          terminalSaveOutcome.current = { kind: "closed" };
        } else {
          setSaveWarning("network");
          return { kind: "retryable-error" };
        }
        return terminalSaveOutcome.current!;
      }
    },
    [saveMutation, session.id, clearRetryTimer, resyncCountdown, abandoningRef],
  );

  /** Every caller joins the entire drain, rather than only one of its PUTs. */
  const flushLatest = useCallback((): Promise<FlushOutcome> => {
    if (!mountedRef.current || !ownsDraftLifetime()) return Promise.resolve({ kind: "unmounted" });
    if (abandoningRef.current) return Promise.resolve({ kind: "abandoning" });
    if (drainPromiseRef.current) return drainPromiseRef.current;
    const operation = Promise.resolve().then(async (): Promise<FlushOutcome> => {
      if (terminalSaveOutcome.current) return terminalSaveOutcome.current;
      while (editGeneration.current !== confirmedGeneration.current) {
        if (!mountedRef.current || !ownsDraftLifetime()) return { kind: "unmounted" };
        if (abandoningRef.current) return { kind: "abandoning" };
        const sentGeneration = editGeneration.current;
        const sentAnswers = answersRef.current;
        const result = await persistSnapshot(sentAnswers, sentGeneration);
        if (result.kind !== "saved") return result;
      }
      return { kind: "saved" };
    });
    drainPromiseRef.current = operation;
    const teardown = () => {
      if (drainPromiseRef.current === operation) drainPromiseRef.current = null;
    };
    void operation.then(teardown, teardown);
    return operation;
  }, [persistSnapshot, abandoningRef]);

  /** One synchronous guard owns manual, timer and server-expired closure. */
  const submit = useCallback(
    async (automatic = false, knownOutcome?: FlushOutcome) => {
      if (
        !mountedRef.current ||
        !ownsDraftLifetime() ||
        abandoningRef.current ||
        closingRef.current
      )
        return;
      closingRef.current = true;
      setClosing(true);
      if (automatic) autoSubmitted.current = true;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      clearRetryTimer();
      let completed = false;
      try {
        let outcome = knownOutcome ?? (await flushLatest());
        // Guard again after every await. Edits are blocked while closing, but
        // this also protects future snapshot callers from overtaking the POST.
        while (outcome.kind === "saved" && editGeneration.current !== confirmedGeneration.current) {
          outcome = await flushLatest();
        }
        if (!mountedRef.current || !ownsDraftLifetime() || abandoningRef.current) return;
        if (outcome.kind === "closed") {
          toast.show(t("error.EXAM_SUBMITTED"), "info");
          navigate(`/exam/${session.id}/grade`, { replace: true });
          completed = true;
        } else if (outcome.kind === "expired") {
          autoSubmitted.current = true;
          toast.show(t("error.EXAM_EXPIRED"), "info");
          completed = await submitNow();
        } else if (outcome.kind === "saved" || (automatic && outcome.kind === "retryable-error")) {
          // The countdown still closes on time if the network save failed;
          // submitNow retains and explains any unconfirmed local answers.
          completed = await submitNow();
        } else if (outcome.kind === "retryable-error") {
          toast.error(t("exam.session.submitSaveFailed"));
        }
      } finally {
        // Keep the parent's hold through navigation: releasing it in this
        // same tick lets a /current 404 overwrite the auto-submit route state.
        if (!completed) {
          closingRef.current = false;
          if (mountedRef.current) setClosing(false);
        }
      }
    },
    [
      flushLatest,
      clearRetryTimer,
      submitNow,
      abandoningRef,
      closingRef,
      toast,
      t,
      navigate,
      session.id,
    ],
  );

  /** Ordinary autosave alone schedules backoff or delegates terminal closure. */
  const flushSave = useCallback(async () => {
    // Only submission may drain while closing. Ordinary triggers must check
    // ownership before they can create a new PUT, not just after it settles.
    if (!mountedRef.current || !ownsDraftLifetime() || abandoningRef.current || closingRef.current)
      return;
    const outcome = await flushLatest();
    if (!mountedRef.current || !ownsDraftLifetime() || abandoningRef.current || closingRef.current)
      return outcome;
    if (outcome.kind === "expired" || outcome.kind === "closed") {
      await submit(outcome.kind === "expired", outcome);
    } else if (outcome.kind === "retryable-error" && !retryTimer.current) {
      const attempt = retryAttempt.current++;
      const delay = SAVE_RETRY_BACKOFF_MS[Math.min(attempt, SAVE_RETRY_BACKOFF_MS.length - 1)];
      retryTimer.current = setTimeout(() => {
        retryTimer.current = null;
        void flushSave();
      }, delay);
    }
    return outcome;
  }, [flushLatest, submit, abandoningRef, closingRef]);

  function replaceAnswers(next: Record<string, AnswerEntry>) {
    if (!mountedRef.current || !ownsDraftLifetime() || closingRef.current || abandoningRef.current)
      return;
    answersRef.current = next;
    editGeneration.current += 1;
    setAnswers(next);
    setSaveState("saving");
    saveDraft(session.id, {
      baseRevision: revisionRef.current,
      answers: next,
      updatedAt: Date.now(),
    });
  }

  function scheduleSave() {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void flushSave(), SAVE_DEBOUNCE_MS);
  }

  useEffect(() => {
    function onBeforeUnload() {
      if (editGeneration.current !== confirmedGeneration.current) void flushSave();
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [flushSave]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (clockJumpTimeoutRef.current) clearTimeout(clockJumpTimeoutRef.current);
    };
  }, []);

  /**
   * "Avbryt övningstentan", after the confirm dialog. `abandoningRef` is set
   * first, synchronously: `Button`'s `loading` does not disable the button
   * (UX-03), so a second click in the same tick still reaches this handler.
   * Pending saves are dropped (an abandoned paper is not graded); a save
   * already in flight is ignored when it settles (see `flushSave`).
   */
  async function abandon() {
    if (abandoningRef.current || closingRef.current) return;
    abandoningRef.current = true;
    setAbandoning(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    clearRetryTimer();
    try {
      await abandonMutation.mutateAsync();
    } catch (error) {
      abandoningRef.current = false;
      setAbandoning(false);
      setAbandonOpen(false);
      if (isCode(error, "EXAM_EXPIRED")) {
        // The time ran out first: the paper is gradable, so take the time-out path.
        toast.show(t("error.EXAM_EXPIRED"), "info");
        if (!autoSubmitted.current) {
          void submit(true, { kind: "expired" });
        }
      } else if (isCode(error, "EXAM_SUBMITTED")) {
        // Handed in from another tab or device.
        toast.show(t("error.EXAM_SUBMITTED"), "info");
        if (mountedRef.current) navigate(`/exam/${session.id}/grade`, { replace: true });
      } else {
        toast.error(messageForError(error, t));
        if (editGeneration.current !== confirmedGeneration.current) void flushSave();
      }
      return;
    }
    // `abandoningRef` stays true: this component is leaving.
    clearDraft(session.id);
    toast.success(t("exam.session.abandoned"));
    if (mountedRef.current) navigate("/exam", { replace: true, state: { abandoned: true } });
  }

  useEffect(() => {
    // Recomputed fresh from the anchor, not the rendered `remainingMs`: that
    // value is memoized on `[tick]` alone, so a render triggered solely by
    // `resyncPending` clearing (no new tick yet) would otherwise still see
    // the stale, pre-resync figure — exactly the render this effect must
    // act on correctly, since `resyncPending` just went from blocking to not.
    const freshRemainingMs = anchorRef.current.remainingMs - elapsedSinceAnchor(anchorRef.current);
    // Time wins over an unconfirmed abandon dialog, but an abandon already
    // requested holds the auto-submit until it settles (`abandoning` is a
    // dependency so a refused abandon re-runs this check).
    if (
      freshRemainingMs <= 0 &&
      !autoSubmitted.current &&
      !resyncPending &&
      !abandoningRef.current &&
      !closingRef.current
    ) {
      setAbandonOpen(false);
      setConfirmOpen(false);
      void submit(true);
    }
    // `resyncPending` is React state (rereview-O2.md R2-1d), so clearing it
    // is guaranteed to re-run this check — not left to `submit`'s identity
    // happening to change on the same render.
  }, [remainingMs, submit, resyncPending, abandoning, abandoningRef, closing, closingRef]);

  function changeIndex(next: number) {
    if (!mountedRef.current || !ownsDraftLifetime() || closingRef.current || abandoningRef.current)
      return;
    if (editGeneration.current !== confirmedGeneration.current) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      void flushSave();
    }
    setIndex(next);
  }

  function updateAnswer(questionId: string, patch: Partial<AnswerEntry>) {
    if (!mountedRef.current || !ownsDraftLifetime() || closingRef.current || abandoningRef.current)
      return;
    replaceAnswers({
      ...answersRef.current,
      [questionId]: { ...answersRef.current[questionId], ...patch },
    });
    scheduleSave();
  }

  const current = session.questions[index];
  const currentAnswer = answers[current.question.id] ?? {};

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="sr-only">
        {t("exam.session.srTitle", { slot: current.slot, total: session.questions.length })}
      </h1>

      <Modal
        open={!!restorePrompt}
        onClose={() => setRestorePrompt(null)}
        title={t("exam.session.restore.title")}
        description={t("exam.session.restore.body")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRestorePrompt(null)}>
              {t("exam.session.restore.discard")}
            </Button>
            <Button
              onClick={() => {
                if (restorePrompt) {
                  if (closingRef.current || abandoningRef.current) return;
                  replaceAnswers(restorePrompt);
                  void flushSave();
                }
                setRestorePrompt(null);
              }}
            >
              {t("exam.session.restore.restore")}
            </Button>
          </>
        }
      />

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={t("exam.session.submitConfirm.title")}
        description={t("exam.session.submitConfirm.body")}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                void submit();
              }}
              loading={closing}
              disabled={closing || abandoning}
            >
              {t("exam.session.submitConfirm.confirm")}
            </Button>
          </>
        }
      />

      <Modal
        open={abandonOpen}
        onClose={() => {
          if (!abandoning) setAbandonOpen(false);
        }}
        title={t("exam.session.abandonConfirm.title")}
        description={t("exam.session.abandonConfirm.body")}
        footer={
          <>
            <Button variant="secondary" disabled={abandoning} onClick={() => setAbandonOpen(false)}>
              {t("exam.session.abandonConfirm.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={() => void abandon()}
              loading={abandoning}
              disabled={closing}
            >
              {t("exam.session.abandonConfirm.confirm")}
            </Button>
          </>
        }
      />

      {lastPlan && !planHidden && (
        <LastPlanNote
          plan={lastPlan}
          headingLevel={2}
          className="mb-4"
          onHide={() => {
            instructionRef.current?.focus();
            hideLastPlan(session.id);
            setPlanHidden(true);
          }}
        />
      )}

      {/*
        Review-A Important 4: only the badge+timer group is sticky — the
        instruction sentence used to sit inside the same sticky element,
        which at 390 px wrapped to about three lines and, together with the
        64 px sticky app header, covered roughly a quarter of a phone
        viewport permanently (more at 200% zoom). The instruction is now an
        ordinary, scrolling paragraph; `scroll-padding-top` below (on the
        page's own root) keeps a field focused while tabbing upwards from
        landing underneath the sticky bar (WCAG 2.4.11).
      */}
      <p ref={instructionRef} tabIndex={-1} className="mb-2 text-sm text-ink-600 dark:text-ink-400">
        {t("exam.session.instruction")}{" "}
        {/* Ruling 6 (slice D): the formula sheet is an allowed aid, as on the real exam. A new tab
            keeps the session, its clock and its unsaved draft where they are. */}
        <a
          href="/library/formulas"
          target="_blank"
          rel="noopener"
          className="font-medium text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
        >
          {t("exam.session.formulas")}
        </a>
      </p>
      {/*
        UX-01: this used to be a ticking `aria-live` element, re-announcing
        "1:29:59, 1:29:58, …" for up to 5 hours and drowning the problem
        text. `role="timer"` reads only on request; the crossing effect
        above speaks a one-time `role="status"` at 30/5/1 minutes instead.
        The row is sticky (UX-07) so the clock stays visible on a phone,
        where a single problem can be 1500–2500 px tall, and the warning
        badges below are text, not colour alone (WCAG 1.4.1).
      */}
      <div className="sticky top-16 z-20 mb-4 flex items-center justify-end gap-2 rounded-lg bg-white/95 px-3 py-2 backdrop-blur dark:bg-ink-950/95">
        {/*
          Review-A Important 5: the badge used to show for the *whole* band
          below a threshold ("30 min kvar" at 12:00 left is false), and
          stopped at 5 minutes with no visible counterpart to the spoken
          1-minute warning. "Under N min kvar" is true throughout its band,
          and warn1 gets its own badge to match.
        */}
        {level !== "normal" && level !== "over" && (
          <Badge tone={level === "warn30" ? "warning" : "danger"}>
            {level === "warn30"
              ? t("exam.session.warn30Badge")
              : level === "warn5"
                ? t("exam.session.warn5Badge")
                : t("exam.session.warn1Badge")}
          </Badge>
        )}
        <div
          role="timer"
          aria-label={t("exam.session.timeRemaining")}
          className={
            level === "over" || level === "warn5" || level === "warn1"
              ? "font-display text-lg text-red-700 dark:text-red-400"
              : level === "warn30"
                ? "font-display text-lg text-amber-700 dark:text-amber-400"
                : "font-display text-lg text-ink-900 dark:text-ink-50"
          }
        >
          {formatDuration(Math.max(0, remainingMs))}
        </div>
      </div>
      {/*
        Always mounted, starting empty (review-A Important 2) — a
        role="status" that appears already holding text is unreliably
        announced by some screen readers, which would drop the first
        warning of every sitting.
      */}
      <p role="status" className="sr-only">
        {announcement}
      </p>

      {saveWarning === "network" && (
        <p
          role="status"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
        >
          {t("exam.session.saveError")}
        </p>
      )}
      {saveWarning === "expired" && (
        <p
          role="status"
          className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {t("exam.session.sessionExpired")}{" "}
          <Link
            to="/login"
            state={{ from: "/exam/session" }}
            className="font-medium underline underline-offset-2"
          >
            {t("auth.signIn")}
          </Link>
        </p>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {session.questions.map((q, i) => {
          const answered = !!answers[q.question.id]?.final;
          return (
            <button
              key={q.slot}
              type="button"
              disabled={closing || abandoning}
              onClick={() => changeIndex(i)}
              className={
                i === index
                  ? "rounded-lg border border-accent-600 bg-accent-100 px-3 py-1.5 max-sm:min-h-11 max-sm:min-w-11 text-sm font-medium text-accent-900 dark:border-accent-500 dark:bg-accent-900 dark:text-accent-50"
                  : "rounded-lg border border-ink-300 px-3 py-1.5 max-sm:min-h-11 max-sm:min-w-11 text-sm text-ink-700 hover:bg-ink-100 dark:border-ink-700 dark:text-ink-200 dark:hover:bg-ink-800"
              }
            >
              {q.slot}
              {answered && <Check size={12} className="ml-1 inline" aria-hidden="true" />}
              <span
                className={
                  i === index
                    ? "ml-1 text-xs text-ink-700 dark:text-ink-200"
                    : "ml-1 text-xs text-ink-500 dark:text-ink-400"
                }
              >
                ({formatPoints(q.points, lang)})
              </span>
            </button>
          );
        })}
      </div>

      <Card
        title={t("exam.session.problemTitle", {
          slot: current.slot,
          total: session.questions.length,
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <SourceBadge source={current.question.source} />
            <Badge tone="neutral">{formatPoints(current.points, lang)}</Badge>
          </div>
        }
      >
        <TranslationPendingBadge pending={current.question.translationPending} className="mb-2" />
        <Localized value={current.question.statement} />
        {current.question.given.length > 0 && (
          <div className="mt-4 space-y-2 rounded-lg bg-ink-100 p-3 text-sm dark:bg-ink-800/60">
            {current.question.given.map((item, i) => (
              <Localized key={i} value={item} variant="compact" />
            ))}
          </div>
        )}
        {/* Part-tagged figures are drawn under their part by QuestionParts. */}
        {current.question.figures
          .filter((figure) => !figure.partId)
          .map((figure, i) => (
            <Figure key={i} figure={figure} />
          ))}
        {/* The statement of a real exam problem is often just a lead-in — the
            sub-questions live in `parts[].prompt` and must be on the paper. */}
        {current.question.type !== "mc" && (
          <QuestionParts parts={current.question.parts} figures={current.question.figures} />
        )}

        <div className="mt-6 space-y-4">
          <Input
            label={t("exam.session.answerLabel")}
            disabled={closing || abandoning}
            value={currentAnswer.final ?? ""}
            onChange={(event) => updateAnswer(current.question.id, { final: event.target.value })}
            onBlur={() => {
              if (saveTimer.current) clearTimeout(saveTimer.current);
              void flushSave();
            }}
          />
          <Textarea
            label={t("exam.session.notesLabel")}
            disabled={closing || abandoning}
            hint={t("exam.session.notesHint")}
            value={currentAnswer.notes ?? ""}
            onChange={(event) => updateAnswer(current.question.id, { notes: event.target.value })}
            onBlur={() => {
              if (saveTimer.current) clearTimeout(saveTimer.current);
              void flushSave();
            }}
            rows={3}
          />
        </div>

        <ReportIssue questionId={current.question.id} className="mt-4" />
      </Card>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-ink-500 dark:text-ink-400" aria-live="polite">
          {saveState === "saved"
            ? t("common.saved")
            : saveState === "saving"
              ? t("common.saving")
              : ""}
        </p>
        {/*
          UX-06: "Lämna in övningstentan" used to exist only on the last problem, so
          handing in early meant clicking through every remaining problem
          first. It is now reachable from every problem — secondary while
          there is still a "Nästa" to go forward with, primary once it is
          the only way on.
        */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            disabled={index === 0 || closing || abandoning}
            onClick={() => changeIndex(Math.max(0, index - 1))}
          >
            {t("common.back")}
          </Button>
          {index < session.questions.length - 1 && (
            <Button
              disabled={closing || abandoning}
              onClick={() => changeIndex(Math.min(session.questions.length - 1, index + 1))}
            >
              {t("common.next")}
            </Button>
          )}
          <Button
            variant={index < session.questions.length - 1 ? "secondary" : "primary"}
            disabled={closing || abandoning}
            onClick={() => setConfirmOpen(true)}
          >
            {t("exam.session.submit")}
          </Button>
        </div>
      </div>

      {/*
        Its own row below the hand-in row, behind a border and a wide gap, so a
        thumb aiming for "Lämna in" never lands on it (mis-tap on phones).
      */}
      <div className="mt-8 border-t border-ink-200 pt-4 dark:border-ink-800">
        <Button
          variant="ghost"
          className="max-sm:min-h-11"
          disabled={closing || abandoning}
          onClick={() => setAbandonOpen(true)}
        >
          {t("exam.session.abandon")}
        </Button>
      </div>
    </div>
  );
}

export default ExamSessionPage;
